import React, { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import InternationalPhoneInput from '../../common/InternationalPhoneInput';
import './MultiCategoryTicketForm.scss';

interface Category {
  id: number;
  name: string;
  price: number | string;
  description?: string;
  sold_out: boolean;
  remaining_display?: string;
  hidden: boolean;
}

interface EventField {
  id: number;
  name: string;
  label: string;
  field_type: string;
  required: boolean;
  options_dict?: { [key: string]: string };
}

interface MultiCategoryTicketFormProps {
  event: any;
  categories?: Category[];
  loading?: boolean;
  error?: string | null;
}

const MultiCategoryTicketForm: React.FC<MultiCategoryTicketFormProps> = ({ 
  event, 
  categories = [], 
  loading = false, 
  error = null 
}) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [quantities, setQuantities] = useState<{ [key: number]: number }>({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [customFields, setCustomFields] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Initialize quantities
  useEffect(() => {
    console.log('MultiCategoryTicketForm - Categories received:', categories);
    const initialQuantities: { [key: number]: number } = {};
    categories.forEach(category => {
      if (!category.hidden) {
        initialQuantities[category.id] = 0;
      }
    });
    setQuantities(initialQuantities);
  }, [categories]);

  // Prefill form data when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const fullName = `${user.first_name} ${user.last_name}`.trim();
      const userPhone = user.profile?.phone_number || '';
      
      setFormData(prev => ({
        ...prev,
        name: fullName || prev.name,
        email: user.email || prev.email,
        phone: userPhone || prev.phone,
      }));
      
      // Show a brief success message when form is prefilled
      console.log('Form prefilled with user data:', { fullName, email: user.email, phone: userPhone });
    }
  }, [isAuthenticated, user]);

  const incrementQuantity = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category || category.sold_out) return;

    setQuantities(prev => ({
      ...prev,
      [categoryId]: Math.min((prev[categoryId] || 0) + 1, 10) // Max 10 per category
    }));
  };

  const decrementQuantity = (categoryId: number) => {
    setQuantities(prev => ({
      ...prev,
      [categoryId]: Math.max((prev[categoryId] || 0) - 1, 0)
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData(prev => ({
      ...prev,
      phone: value || ''
    }));
  };

  const handleCustomFieldChange = (fieldName: string, value: string) => {
    setCustomFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleQuantityChange = (categoryId: number, value: string) => {
    const numValue = parseInt(value) || 0;
    const category = categories.find(c => c.id === categoryId);
    if (!category || category.sold_out) return;

    setQuantities(prev => ({
      ...prev,
      [categoryId]: Math.min(Math.max(numValue, 0), 10)
    }));
  };

  const getTotalQuantity = () => {
    return Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  };

  const getTotalPrice = () => {
    return categories.reduce((total, category) => {
      const qty = quantities[category.id] || 0;
      const numericPrice = typeof category.price === 'string' ? parseFloat(category.price) : category.price;
      return total + (numericPrice * qty);
    }, 0);
  };

  const formatPrice = (price: number | string) => {
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `${event?.setting?.currency?.symbol || '$'} ${numericPrice.toFixed(2)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (getTotalQuantity() === 0) {
      setSubmitError('Please select at least one ticket');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const selectedCategories = categories
        .filter(category => !category.hidden && quantities[category.id] > 0)
        .map(category => ({
          category_id: category.id,
          quantity: quantities[category.id],
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          custom_fields: customFields
        }));

      const registrationData = {
        event_slug: event.slug,
        categories: selectedCategories
      };

      const response = await apiService.registration.multi(registrationData);
      
      if (response.success) {
        // Redirect to cart or show success message
        window.location.href = '/cart';
      } else {
        setSubmitError(response.message || 'Registration failed');
      }
    } catch (error: any) {
      setSubmitError(error.message || 'An error occurred during registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCustomField = (field: EventField) => {
    if (field.field_type === 'select') {
      return (
        <select
          className="form-select"
          name={field.name}
          id={field.name}
          value={customFields[field.name] || ''}
          onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
          required={field.required}
        >
          <option value="">Select {field.label}</option>
          {field.options_dict && Object.entries(field.options_dict).map(([key, value]) => (
            <option key={value} value={value}>{key}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={field.field_type}
        className="form-control"
        name={field.name}
        id={field.name}
        placeholder={field.label}
        value={customFields[field.name] || ''}
        onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
        required={field.required}
      />
    );
  };

  if (loading) {
    return (
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading ticket form...</span>
          </div>
        </div>
    );
  }

  if (error) {
    return (
    
        <div className="alert alert-warning" role="alert">
          <strong>Ticket Form Loading Error:</strong> {error}
        </div>

    );
  }

  return (
      <form onSubmit={handleSubmit}>
        {/* Quota Warning */}
        {event?.quota === 0 && (
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Registration unavailable: Your event quota has been reached.
          </div>
        )}

        {/* Categories */}
        <div className="categories-section">
          {categories && categories.length > 0 ? (
            categories
              .filter(category => !category.hidden)
              .map(category => (
              <div 
                key={category.id} 
                className={`category-item ${category.sold_out ? 'sold-out' : ''} ${quantities[category.id] > 0 ? 'selected' : ''}`}
              >
                <div className="category-header">
                  <div className="category-info">
                    <h6>{category.name}</h6>
                    <p className="price">{formatPrice(category.price)}</p>
                    {event?.setting?.show_remaining_passes && category.remaining_display && (
                      <p className="remaining text-muted">
                        <i className="fas fa-ticket-alt me-1"></i>
                        {category.remaining_display}
                      </p>
                    )}
                  </div>
                  <div className="quantity-controls">
                    <button
                      type="button"
                      onClick={() => decrementQuantity(category.id)}
                      disabled={category.sold_out || quantities[category.id] <= 0}
                      className="btn-quantity"
                    >
                      <i className="fas fa-minus"></i>
                    </button>
                    <input
                      type="number"
                      id={`quantity_${category.id}`}
                      value={quantities[category.id] || 0}
                      min="0"
                      max="10"
                      disabled={category.sold_out}
                      onChange={(e) => handleQuantityChange(category.id, e.target.value)}
                      className="quantity-input"
                    />
                    <button
                      type="button"
                      onClick={() => incrementQuantity(category.id)}
                      disabled={category.sold_out || quantities[category.id] >= 10}
                      className="btn-quantity"
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                </div>
                {category.description && (
                  <p className="category-description">{category.description}</p>
                )}
              </div>
            ))
          ) : (
            <div className="alert alert-warning text-center">
              <i className="fas fa-exclamation-circle me-2"></i>
              No ticket categories are currently available for this event.
            </div>
          )}
        </div>

        {/* Personal Information */}
        <div className="form-section">
          <h5>
            Personal Information
            {isAuthenticated && user && (
              <small className="text-muted ms-2">
                <i className="fas fa-user-check me-1"></i>
                Prefilled from your account
              </small>
            )}
          </h5>
          
          {!authLoading && !isAuthenticated && (
            <div className="alert alert-info mb-3">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Quick Checkout:</strong> 
              <a href="/accounts/login" className="alert-link ms-1">Sign in</a> to automatically fill your information and save time!
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${isAuthenticated && user && formData.name ? 'prefilled' : ''}`}
              name="name"
              id="name"
              placeholder="Pass Holder Name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              className={`form-control ${isAuthenticated && user && formData.email ? 'prefilled' : ''}`}
              name="email"
              id="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              Phone <span className="text-danger">*</span>
            </label>
            <InternationalPhoneInput
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="Phone Number"
              id="phone"
              name="phone"
              className={isAuthenticated && user && formData.phone ? 'prefilled' : ''}
            />
          </div>
        </div>

        {/* Custom Fields */}
        {event?.fields && event.fields.length > 0 && (
          <div className="form-section">
            <h5>Additional Information</h5>
            {event.fields.map((fieldItem: any) => {
              const field = fieldItem.field;
              return (
                <div key={field.id} className="form-group">
                  <label htmlFor={field.name} className="form-label">
                    {field.label} {field.required && <span className="text-danger">*</span>}
                  </label>
                  {renderCustomField(field)}
                </div>
              );
            })}
          </div>
        )}

        {/* Total Summary */}
        {getTotalQuantity() > 0 && (
          <div className="total-summary">
            <h6 className="summary-title">Order Summary</h6>
            {categories
              .filter(category => !category.hidden && quantities[category.id] > 0)
              .map(category => (
                <div key={category.id} className="category-summary-row">
                  <span>{category.name} × {quantities[category.id]}</span>
                  <span>{formatPrice((typeof category.price === 'string' ? parseFloat(category.price) : category.price) * quantities[category.id])}</span>
                </div>
              ))}
            <div className="total-row">
              <span>Total Tickets:</span>
              <span>{getTotalQuantity()}</span>
            </div>
            <div className="total-row total-price">
              <span>Total Price:</span>
              <span>{formatPrice(getTotalPrice())}</span>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="text-center mt-4">
          {event?.quota === 0 ? (
            <div className="alert alert-warning">
              You've reached the quota for this event. Check your{' '}
              <a href="/cart" className="alert-link">cart</a> to see if you already have tickets.
            </div>
          ) : (
            <>
              {submitError && (
                <div className="alert alert-danger mb-3">
                  {submitError}
                </div>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={getTotalQuantity() === 0 || isSubmitting || event?.quota === 0}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Processing...
                  </>
                ) : (
                  event?.content?.button_label || 'Add to Cart'
                )}
              </button>
            </>
          )}
        </div>
      </form>
  );
};

export default MultiCategoryTicketForm;
